import { VerydiaApiError, VerydiaConnectionError, VerydiaControlClient } from '@verydia/sdk';
import { useQuery } from '@tanstack/react-query';

/**
 * The console talks to the API through the published SDK, not through a private
 * fetch wrapper. Whatever an external integrator experiences — retries, Problem
 * Details decoding, request-id capture — the console experiences first, so a rough
 * edge in the SDK shows up in our own tool before it shows up in a customer's.
 */
const baseUrl: string = import.meta.env.VITE_VC_API_BASE_URL ?? 'http://localhost:8787';

/**
 * Supplies the bearer token for every authenticated request.
 *
 * A module-level hook rather than a constructor argument because the client is
 * created once and the token changes: it is refreshed on expiry and cleared on
 * sign-out. The auth provider installs the getter on mount, so the SDK asks for
 * a fresh token per request instead of holding a stale one.
 *
 * WHICH token is decided by `apiBearerToken` in `@verydia/contracts`, which the
 * API’s verifier is configured from as well.
 */
let apiTokenSource: (() => Promise<string | null>) | null = null;

export function useApiTokenSource(source: () => Promise<string | null>): void {
  apiTokenSource = source;
}

export const verydia = new VerydiaControlClient({
  baseUrl,
  // The console is interactive: a spinner that hangs for three retries is worse
  // than an error the operator can act on.
  maxRetries: 1,
  // Aurora scales to zero, so the first request after an idle period waits for
  // the cluster to resume. Eight seconds would turn a normal cold start into a
  // visible failure.
  timeoutMs: 30_000,
  userAgent: 'verydia-control-console/0.1.0',
  authorization: async () => (apiTokenSource === null ? null : apiTokenSource()),
});

export const apiBaseUrl = baseUrl;

export interface ApiFailure {
  message: string;
  requestId: string | null;
  kind: 'unreachable' | 'error' | 'starting';
  /** Seconds the server asked us to wait. Only set when `kind` is 'starting'. */
  retryAfterSeconds: number | null;
}

/**
 * How many times the console will wait for the control plane to wake.
 *
 * The API already absorbs a resume itself; reaching the console at all means
 * that budget was spent. A small bounded number of further attempts covers a
 * genuinely slow wake without becoming an infinite spinner (§15).
 */
export const STARTUP_RETRY_LIMIT = 4;

/** Turns any thrown value into something the UI can render without guessing. */
export function describeFailure(error: unknown): ApiFailure {
  if (error instanceof VerydiaConnectionError) {
    return {
      kind: 'unreachable',
      message: `Could not reach the API at ${baseUrl}. Check that it is running and reachable from this browser.`,
      requestId: null,
      retryAfterSeconds: null,
    };
  }
  if (error instanceof VerydiaApiError) {
    // A waking control plane is not a failure, and must not be rendered as one.
    // Aurora scales to zero to keep this deployment near free, so the first
    // request after a quiet period legitimately waits (§14).
    if (error.isStarting) {
      return {
        kind: 'starting',
        message: error.message,
        requestId: error.requestId,
        retryAfterSeconds: error.retryAfterSeconds,
      };
    }
    return {
      kind: 'error',
      message: error.message,
      requestId: error.requestId,
      retryAfterSeconds: null,
    };
  }
  return {
    kind: 'error',
    message: error instanceof Error ? error.message : 'An unexpected error occurred.',
    requestId: null,
    retryAfterSeconds: null,
  };
}

/** True when this failure is worth waiting out rather than reporting. */
export function isStarting(error: unknown): boolean {
  return error instanceof VerydiaApiError && error.isStarting;
}

export function useHealth() {
  return useQuery({
    queryKey: ['health'],
    queryFn: () => verydia.health(),
    // The overview doubles as an at-a-glance status board, so it refreshes on its
    // own. Thirty seconds is frequent enough to notice an outage and infrequent
    // enough to stay invisible in the API's request logs.
    refetchInterval: 30_000,
    retry: false,
  });
}

export function usePlatformInfo() {
  return useQuery({
    queryKey: ['platform-info'],
    queryFn: () => verydia.platformInfo(),
    // Deployment metadata changes only on release.
    staleTime: 5 * 60_000,
    retry: false,
  });
}

/* --------------------------------- identity -------------------------------- */

/**
 * Every identity query is keyed so that a mutation can invalidate exactly what
 * it changed. Suspending a member must refresh that organization's member list
 * and nothing else — a blanket invalidation would refetch the audit log and the
 * organization list on every role change.
 */
export const queryKeys = {
  me: ['identity', 'me'] as const,
  organizations: ['identity', 'organizations'] as const,
  organization: (id: string) => ['identity', 'organization', id] as const,
  members: (id: string) => ['identity', 'members', id] as const,
  invitations: (id: string) => ['identity', 'invitations', id] as const,
  platformUsers: ['identity', 'platform-users'] as const,
  platformAudit: ['identity', 'platform-audit'] as const,
  organizationAudit: (id: string) => ['identity', 'organization-audit', id] as const,
  products: (organizationId: string) => ['catalog', 'products', organizationId] as const,
  /*
    Organization-scoped, and deliberately nothing else.

    No product or environment id belongs in this key. The Overview reports the
    organization as a whole, so a key that varied with the selected product would
    quietly turn it into a filtered view — the exact ambiguity Checkpoint F
    exists to remove.
  */
  catalogSummary: (organizationId: string) => ['catalog', 'summary', organizationId] as const,
  // Organization-scoped, like the customers themselves. No product id belongs
  // here: a customer is a sibling of Product, not a descendant.
  customers: (organizationId: string) => ['customers', organizationId] as const,
  product: (organizationId: string, productId: string) =>
    ['catalog', 'product', organizationId, productId] as const,
  // Keyed by BOTH ids. A key of just the product id would collide across
  // organizations for anyone who is a member of more than one.
  productActivity: (organizationId: string, productId: string) =>
    ['catalog', 'product-activity', organizationId, productId] as const,
  environments: (organizationId: string, productId: string) =>
    ['catalog', 'environments', organizationId, productId] as const,
  plans: (organizationId: string, productId: string) =>
    ['catalog', 'plans', organizationId, productId] as const,
};

export function useMe(enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.me,
    queryFn: () => verydia.me(),
    enabled,
    // Memberships and roles change rarely, but a suspension must take effect
    // without a reload — so this refetches on focus rather than caching for long.
    staleTime: 30_000,
    // Retried ONLY while the control plane is starting, and only a few times.
    // Every other failure surfaces on the first attempt: retrying a 403 wastes
    // the user's time and hides the answer.
    retry: (failureCount, error) => isStarting(error) && failureCount < STARTUP_RETRY_LIMIT,
    retryDelay: (_attempt, error) => {
      const seconds = error instanceof VerydiaApiError ? (error.retryAfterSeconds ?? 3) : 3;
      return seconds * 1000;
    },
  });
}
